/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { Link, ResponsiveHeroImage, ReviewRating, LazyLoadImages } from "@quintype/components";
import { ImageFallback } from "../image-fallback.js";
import { GlideCarousel } from "../../../components/atoms/glide";
import { CollectionName } from "../../atoms/collection-name/index.js";
import TimeAgo from "react-timeago";
import "./full-screen-slider.module.css";
import { breakpoint } from "../../../../utils/breakpoint";
import {
  getNumberOfStoriesToShow,
  altTagOfHeroImages,
  isExternalStory,
  formatter,
  getStoryUrl,
  getScrollSpeed
} from "../../../utils";
import { computeTimings, markTiming, TIMING_KEYS } from "../../../user-timing";
import { SvgHandler } from "../../svg-handler/svg-handler";
import dotIcon from "../../../../assets/icons/dot.svg";

export function Carousel(props) {
  markTiming(TIMING_KEYS["SLIDER_START"]);
  const {
    metadata: { show_collection_name: collectionName = true, number_of_stories_to_show: numberOfStoriesToShow } = {},
    eager = false
  } = props;

  let resizeDone = false;

  const resize = () => {
    if (resizeDone === false) {
      global.dispatchEvent(new global.Event("resize"));
      resizeDone = true;
    }
  };
  const metadata = props.metadata;
  const associatedMetadata = props.associatedMetadata;
  const paginationValue = get(props, ["metadata", "enable pagination - dot or dash"], "dot").toLowerCase();
  const slidePerView = metadata["number_of_slides_to_show"] < 1 ? 1 : metadata["number_of_slides_to_show"];
  const desktopOptions = {
    afterSlide: resize,
    arrow: metadata["show_arrows"],
    dot: paginationValue,
    items: props.stories,
    autoplay: getScrollSpeed(associatedMetadata),
    perView: slidePerView,
    rtl: get(props.config, ["theme-attributes", "language_direction"]) === "right",
    lazyLoad: true,
    collectionName: metadata["show_collection_name"],
    gap: 1,
    onLazyLoad: () => {}
  };

  const mobileOptions = {
    afterSlide: resize,
    arrow: false,
    dot: paginationValue,
    items: props.stories,
    autoplay: getScrollSpeed(associatedMetadata),
    perView: 1,
    rtl: get(props.config, ["theme-attributes", "language_direction"]) === "right",
    lazyLoad: true,
    collectionName: metadata["show_collection_name"],
    gap: 1,
    onLazyLoad: () => {}
  };

  const options = breakpoint("tablet") ? { ...mobileOptions } : { ...desktopOptions };

  const showNumberOfStoriesToShow = getNumberOfStoriesToShow(numberOfStoriesToShow);
  const SliderWrapper = (
    <GlideCarousel {...options}>
      {props.stories.slice(0, showNumberOfStoriesToShow).map((story, index) => {
        const initialVisibleSlidesEager = index === 0 ? eager : false;
        return (
          <div key={story.id}>
            <CarouselItem
              eager={initialVisibleSlidesEager}
              metadata={props.metadata}
              story={story}
              config={props.config}
              collection={props.collection}
            />
          </div>
        );
      })}
    </GlideCarousel>
  );

  markTiming(TIMING_KEYS["SLIDER_END"]);

  console.log(computeTimings(["SLIDER_START", "SLIDER_END"]));

  return (
    <div styleName="full-screen-slider-wrapper" data-testid="full-screen-component">
      <div styleName="collection-name">
        {collectionName ? (
          <CollectionName name={props.collection.name} config={props.config} section={props.collection} />
        ) : null}
      </div>
      <div
        styleName={`full-screen-slider ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "dots-rtl" : ""
        }`}
      >
        {SliderWrapper}
      </div>
    </div>
  );
}
Carousel.defaultProps = {
  dot: "",
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  numberOfStoriesToShow: 6,
  lazyLoad: true,
  onLazyLoad: () => {}
};

Carousel.propTypes = {
  stories: PropTypes.array,
  config: PropTypes.object,
  collection: PropTypes.object,
  metadata: PropTypes.object,
  eager: PropTypes.bool,
  associatedMetadata: PropTypes.object
};

function CarouselItem(props) {
  if (!props.story) {
    return null;
  }
  const { story, eager, metadata, collection, config } = props;

  return (
    <Link href={getStoryUrl(story)} externalLink={isExternalStory(story)}>
      <Slide eager={eager} metadata={metadata} config={config} story={story} collection={collection} />
    </Link>
  );
}

CarouselItem.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  collection: PropTypes.object,
  metadata: PropTypes.object,
  eager: PropTypes.bool
};

function Slide(props) {
  const {
    metadata: {
      show_section_tag: showSectionTag = true,
      show_author_name: showAuthorName = true,
      show_time_of_publish: showTimeOfPublish = true,
      full_width: fullWidthContainer = true,
      number_of_slides_to_show: numberOfStoriesToShow = 1
    } = {},
    eager
  } = props;
  const aspectRatio = breakpoint("tablet") ? [4, 3] : [3, 1];
  const responsiveFigure = (
    <figure styleName={props.story["story-template"] === "video" ? "video" : ""}>
      <LazyLoadImages margin={"0px"}>
        <ResponsiveHeroImage
          story={props.story}
          aspectRatio={aspectRatio}
          alt={altTagOfHeroImages(props.story)}
          defaultWidth={1024}
          widths={[640, 1024, 1280]}
          sizes="(max-width: 500px) 20vw, (max-width: 768px) 48vw, 100vw"
          imgParams={{ auto: ["format", "compress"], fit: "max" }}
          eager={eager}
        />
      </LazyLoadImages>
    </figure>
  );

  const heroImageTemplate = props.story["hero-image-s3-key"] ? responsiveFigure : <ImageFallback story={props.story} />;
  const isReviewStory = props.story["story-template"] === "review";
  const publishedTime = props.story["last-published-at"]
    ? props.story["last-published-at"]
    : props.story["first-published-at"];

  return (
    <div
      styleName={`full-screen-slider-slide ${
        fullWidthContainer ? "full-screen-slider-fullWidth" : "full-screen-slider-containerWidth"
      }`}
    >
      <div styleName="slide-image">{heroImageTemplate}</div>
      <div styleName="content">
        {showSectionTag ? (
          <div styleName="section" data-testid="fullscreen-sectionname">
            {get(props.story, ["sections", 0, "display-name"]) || get(props.story, ["sections", 0, "name"], "")}
          </div>
        ) : null}
        <div
          styleName={`title ${numberOfStoriesToShow > 2 ? "title-font-small" : "title-font-big"}`}
          dangerouslySetInnerHTML={{ __html: props.story.headline }}
          data-test-id="full-screen-headline"
        />
        {isReviewStory && <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />}
        <div styleName="created-at">
          {showAuthorName ? (
            <div data-testid="fullscreen-authorname">
              {" "}
              BY {get(props.story, ["authors", 0, "name"], props.story["author-name"])}{" "}
            </div>
          ) : null}
          {showAuthorName && showTimeOfPublish ? (
            <SvgHandler xlinkHref={dotIcon} clazzName="dot-icon" width="4px" height="4px" />
          ) : null}
          {showTimeOfPublish ? (
            <div data-testid="fullscreen-showtime">
              <TimeAgo
                date={publishedTime}
                formatter={(value, unit, suffix, date) => formatter(value, unit, suffix, date)}
                title={null}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

Slide.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  metadata: PropTypes.object,
  eager: PropTypes.bool
};
